import React, { useContext, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import PulseLoader from "react-spinners/PulseLoader";
import { useSnackbar } from "notistack";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { jwtDecode } from "jwt-decode";
import { AuthContext } from "../../context/AuthContext";
import { getDataWithToken } from "../../services/GetDataService";
import { deliveryStatusList, statusList } from "../../data";
import RepairForm from "./RepairForm";
import SparepartsForm from "./SparepartsForm";
import { customTextField } from "./technicianStyles";

const dash = (value) => (value ? value : "---------");

const DetailRow = ({ label, children }) => (
  <Box sx={{ mb: 1.5, textAlign: "left" }}>
    <Typography
      variant="small"
      color="text.secondary"
      sx={{ display: "block", mb: 0.25 }}
    >
      {label}
    </Typography>
    <Box sx={{ fontWeight: 500, color: "#0F1624", fontSize: "14px" }}>
      {children}
    </Box>
  </Box>
);

const ChipList = ({ items, getLabel }) => {
  if (!items?.length) {
    return "---------";
  }

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
      {items.map((item, index) => (
        <Chip
          key={`${getLabel(item)}-${index}`}
          size="small"
          label={getLabel(item)}
          variant="outlined"
        />
      ))}
    </Box>
  );
};

const TechnicianDashboard = () => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { ifixit_admin_panel, logout } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [repair, setRepair] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [technicianList, setTechnicianList] = useState([]);

  const myBranchId = jwtDecode(ifixit_admin_panel?.token)?.user?.branch_id;

  const getTechnicianList = async () => {
    const url =
      "/api/v1/user/dropdownlist?designation=Technician&status=true";
    const response = await getDataWithToken(url, ifixit_admin_panel?.token);

    if (response?.status === 401) {
      logout();
      return;
    }

    if (response?.status >= 200 && response?.status < 300) {
      setTechnicianList(response?.data?.data || []);
    }
  };

  useEffect(() => {
    getTechnicianList();
  }, []);

  const handleSnakbarOpen = (msg, vrnt) => {
    enqueueSnackbar(msg, {
      variant: vrnt,
      autoHideDuration: vrnt === "error" ? 3000 : 1000,
    });
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const repairId = search.trim();

    if (!repairId) {
      handleSnakbarOpen("Please enter a repair ID", "error");
      document.getElementById("technician-search")?.focus();
      return;
    }

    setLoading(true);
    setRepair(null);
    setDialogOpen(false);
    setActiveTab(0);

    const url = `/api/v1/repair/find?repair_id=${encodeURIComponent(repairId)}`;
    const response = await getDataWithToken(url, ifixit_admin_panel?.token);
    console.log("repair find response", response);

    if (response?.status === 401) {
      logout();
      return;
    }

    if (response?.status >= 200 && response?.status < 300) {
      const payload = response?.data?.data;
      const foundRepair = Array.isArray(payload) ? payload[0] : payload;

      if (!foundRepair?._id && !foundRepair?.repair_id) {
        handleSnakbarOpen(
          response?.data?.message?.toString() || "No data found",
          "error",
        );
      } else {
        setRepair(foundRepair);
      }
    } else {
      handleSnakbarOpen(
        response?.data?.message?.toString() || "Failed to search repair",
        "error",
      );
    }

    setLoading(false);
  };

  const lastStatus = repair?.repair_status_history_data?.length
    ? repair.repair_status_history_data[
        repair.repair_status_history_data.length - 1
      ]
    : null;

  const repairStatusColor =
    statusList.find((el) => el.name === lastStatus?.repair_status_name)
      ?.color || "";
  const deliveryStatusColor =
    deliveryStatusList?.find(
      (el) => el.name === lastStatus?.repair_delivery_status_name,
    )?.color || "";

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "flex",
        justifyContent: "center",
        alignItems: { xs: "flex-start", sm: "center" },
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 4 },
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
          px: { xs: 2, sm: 3 },
          py: { xs: 2.5, sm: 3 },
          background: "#fff",
          borderRadius: "10px",
          textAlign: "center",
          border: "1px solid #E5E5E5",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 2,
          }}
        >
          <Box
            component="img"
            src="/logo.png"
            alt="iFixit"
            sx={{
              display: "block",
              maxWidth: { xs: 72, sm: 90 },
              width: "auto",
              height: "auto",
            }}
          />
          <Button
            type="button"
            variant="outlined"
            color="error"
            size="small"
            startIcon={<LogoutOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={handleLogout}
            sx={{ minHeight: 32, px: 1.25, fontSize: 12, flexShrink: 0 }}
          >
            Logout
          </Button>
        </Box>
        <Box
          component="form"
          onSubmit={onSubmit}
          sx={{
            display: "flex",
            alignItems: "stretch",
            gap: 1,
            mb: 1.5,
          }}
        >
          <TextField
            sx={{ ...customTextField, flex: 1 }}
            placeholder="Search"
            fullWidth
            size="small"
            id="technician-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              htmlInput: {
                inputMode: "search",
                enterKeyHint: "search",
              },
            }}
          />
          <Button
            variant="contained"
            disableElevation
            size="small"
            type="submit"
            disabled={loading}
            aria-label="Search"
            sx={{ minWidth: 40, minHeight: 40, px: 1, flexShrink: 0 }}
          >
            {loading ? (
              <PulseLoader
                color="#4B46E5"
                loading={loading}
                size={6}
                speedMultiplier={0.5}
              />
            ) : (
              <SearchIcon sx={{ fontSize: 20 }} />
            )}
          </Button>
        </Box>
        {repair && (
          <Box sx={{ textAlign: "left" }}>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              variant="fullWidth"
              sx={{
                mb: 1.5,
                minHeight: 40,
                borderBottom: "1px solid #EAECF0",
                "& .MuiTab-root": {
                  minHeight: 40,
                  fontSize: 14,
                  fontWeight: 500,
                  textTransform: "none",
                },
              }}
            >
              <Tab label="Repair" />
              <Tab label="Spareparts" />
            </Tabs>

            {activeTab === 0 && (
              <RepairForm
                key={repair._id}
                repair={repair}
                token={ifixit_admin_panel?.token}
                logout={logout}
                technicianList={technicianList}
                onNotify={handleSnakbarOpen}
                onRepairUpdated={setRepair}
              />
            )}

            {activeTab === 1 && (
              <SparepartsForm
                key={repair._id}
                repair={repair}
                token={ifixit_admin_panel?.token}
                myBranchId={myBranchId}
                logout={logout}
                onNotify={handleSnakbarOpen}
                onRepairUpdated={setRepair}
              />
            )}

            <Button
              variant="outlined"
              color="info"
              fullWidth
              size="small"
              type="button"
              startIcon={<ListAltOutlinedIcon sx={{ fontSize: 18 }} />}
              onClick={() => setDialogOpen(true)}
              sx={{ minHeight: 40, fontSize: 14 }}
            >
              View Details
            </Button>
          </Box>
        )}
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="sm"
        sx={{
          "& .MuiPaper-root": {
            borderRadius: fullScreen ? 0 : "16px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "20px",
            fontWeight: 600,
            color: "#0F1624",
            position: "relative",
            pr: 6,
            borderBottom: "1px solid #EAECF1",
          }}
        >
          Repair Details ({dash(repair?.repair_id)})
          <IconButton
            aria-label="close"
            onClick={() => setDialogOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2, py: 2 }}>
          <DetailRow label="Date">
            {repair?.created_at
              ? moment(repair.created_at).format("DD/MM/YYYY")
              : "---------"}
          </DetailRow>
          <DetailRow label="Branch">
            {dash(repair?.branch_data?.[0]?.name)}
          </DetailRow>
          <DetailRow label="Customer">
            {dash(repair?.customer_data?.[0]?.name)}
          </DetailRow>
          <DetailRow label="Mobile">
            {dash(repair?.customer_data?.[0]?.mobile)}
          </DetailRow>
          <DetailRow label="Model">
            {dash(repair?.model_data?.[0]?.name)}
          </DetailRow>
          <DetailRow label="Brand">
            {dash(repair?.brand_data?.[0]?.name)}
          </DetailRow>
          <DetailRow label="Serial">{dash(repair?.serial)}</DetailRow>
          <DetailRow label="Pass Code">{dash(repair?.pass_code)}</DetailRow>
          <DetailRow label="Issues">
            <ChipList
              items={repair?.issues}
              getLabel={(item) => `${item.name} - (${item?.repair_cost} TK)`}
            />
          </DetailRow>
          <DetailRow label="Spare Parts">
            <ChipList
              items={repair?.product_details}
              getLabel={(item) => `${item.name} - (${item?.price} TK)`}
            />
          </DetailRow>
          <DetailRow label="Repair Status">
            {lastStatus?.repair_status_name ? (
              <Chip
                label={lastStatus.repair_status_name}
                variant="outlined"
                sx={{ border: 0, backgroundColor: repairStatusColor }}
              />
            ) : (
              "---------"
            )}
          </DetailRow>
          <DetailRow label="Delivery Status">
            {lastStatus?.repair_delivery_status_name ? (
              <Chip
                label={lastStatus.repair_delivery_status_name}
                variant="outlined"
                sx={{ border: 0, backgroundColor: deliveryStatusColor }}
              />
            ) : (
              "---------"
            )}
          </DetailRow>
          <DetailRow label="Repair By">
            {dash(repair?.repair_by_data?.[0]?.name)}
          </DetailRow>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: "1px solid #EAECF1" }}>
          <Button
            variant="outlined"
            color="info"
            onClick={() => setDialogOpen(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TechnicianDashboard;
